import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { getMyOrders } from "../../services/UserApi/OrderApi";
import { resolveImageUrl, MEDIA_BASE_URL } from "../../services/indexMediaBaseUrl";
import { useNavigate } from "react-router-dom";
import { returnOrder, cancelOrder } from "../../services/UserApi/OrderApi";
import OrderDialog from "./OrderDialog";
import MyReviews from "../../components/MyReviews";
import SavedProducts from "../PublicPages/Home/Home/SavedProducts";
import { useTranslation } from "react-i18next";
import MyPaymentsTab from "./MyPaymentsTab";

export default function MyOrdersPage() {
  const { t, i18n } = useTranslation("MyOrdersPage");
  // determine lang like BestSellers example (prefer i18n then localStorage)
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";
  const getName = (p) => (isAr ? (p?.name_ar || p?.name_en) : (p?.name_en || p?.name_ar));

    useEffect(() => {
      document.title = 'DIDA MAX — My Orders';
    }, []);
  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = isAr ? "rtl" : "ltr";
    } catch {}
  }, [lang, isAr]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [returning, setReturning] = useState({});
  const [canceling, setCanceling] = useState({});

  const [activeTab, setActiveTab] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOrderId, setDialogOrderId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchOrders = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getMyOrders();
        const data = res?.data?.data || res?.data || res || [];
        if (mounted) setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || err?.message || t('fetch_orders_failed'));
        console.error("MyOrdersPage:getMyOrders error", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOrders();
    return () => { mounted = false; };
  }, [t]);

  const handleReturn = async (orderId, productId) => {
    const key = `${orderId}_${productId}`;
    try {
      setReturning(prev => ({ ...prev, [key]: true }));
      const res = await returnOrder(orderId, productId);
      window.alert(res?.message || t('return.request_sent'));
    } catch (err) {
      console.error("returnOrder error:", err);
      window.alert(err?.response?.data?.message || err?.message || t('return.request_failed'));
    } finally {
      setReturning(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const isOrderReturnable = (status) => {
    if (!status) return false;
    const s = String(status).toLowerCase();
    return s === "delivered" || s === "confirmed";
  };

  const isOrderCancelable = (status) => {
    if (!status) return false;
    const s = String(status).toLowerCase();
    return s === "pending";
  };

  const handleCancelOrder = async (orderId) => {
    const key = `${orderId}`;
    const confirmMsg = t('confirm.cancel_order');
    if (!window.confirm(confirmMsg)) return;
    try {
      setCanceling(prev => ({ ...prev, [key]: true }));
      const res = await cancelOrder(orderId);
      window.alert(res?.message || t('order.cancel_success'));
      try {
        const refreshed = await getMyOrders();
        const data = refreshed?.data?.data || refreshed?.data || refreshed || [];
        setOrders(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("refresh orders after cancel failed", e);
      }
    } catch (err) {
      console.error("cancelOrder error:", err);
      window.alert(err?.response?.data?.message || err?.message || t('order.cancel_failed'));
    } finally {
      setCanceling(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const openOrderDialog = (orderId) => {
    setDialogOrderId(orderId);
    setDialogOpen(true);
  };

  const closeOrderDialog = () => {
    setDialogOpen(false);
    setDialogOrderId(null);
  };

  const statusConfig = {
    delivered:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: isAr ? "مُسلَّم"  : "Delivered",  Icon: CheckCircleIcon },
    confirmed:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: isAr ? "مؤكد"    : "Confirmed",  Icon: CheckCircleIcon },
    pending:    { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: isAr ? "قيد الانتظار" : "Pending", Icon: LocalShippingIcon },
    shipped:    { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", label: isAr ? "تم الشحن" : "Shipped",   Icon: LocalShippingIcon },
    cancelled:  { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: isAr ? "ملغي"     : "Cancelled", Icon: CancelIcon },
    rejected:   { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: isAr ? "مرفوض"   : "Rejected",  Icon: CancelIcon },
  };

  const getStatus = (s) => statusConfig[String(s || "").toLowerCase()] || {
    color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", label: s || "—", Icon: ShoppingBagIcon,
  };

  const totalSpent = orders.reduce((s, o) => s + (Number(o.total) || 0), 0);
  const deliveredCount = orders.filter((o) => ["delivered","confirmed"].includes(String(o.status||"").toLowerCase())).length;
  const pendingCount   = orders.filter((o) => String(o.status||"").toLowerCase() === "pending").length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", flexDirection: "column", gap: 2 }}>
        <CircularProgress size={56} sx={{ color: "primary.main" }} />
        <Typography color="text.secondary" fontWeight={600}>{isAr ? "جاري تحميل طلباتك..." : "Loading your orders..."}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa" }}>
      {/* Top accent bar */}
      <Box sx={{ height: 3, background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)" }} />

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>

        {/* ── Header ── */}
        <Box sx={{ mb: 4, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: 3,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213,170,47,0.3)",
              }}
            >
              <ShoppingBagIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={900} lineHeight={1.2}>
                {t('my_orders')}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {orders.length} {t('total_orders', { defaultValue: isAr ? 'طلب إجمالاً' : 'orders total' })}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate("/products")}
            sx={{ borderRadius: 3, fontWeight: 700, textTransform: "none", borderWidth: 2, "&:hover": { borderWidth: 2 } }}
          >
            {t('show_all_products', { defaultValue: isAr ? "تسوق الآن" : "Shop Now" })}
          </Button>
        </Box>

        {/* ── Tabs ── */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            mb: 3,
            "& .MuiTabs-indicator": { backgroundColor: "#d5aa2f", height: 3, borderRadius: 2 },
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none", fontSize: "0.95rem", minWidth: 140 },
            "& .Mui-selected": { color: "#d5aa2f !important" },
          }}
        >
          <Tab icon={<ShoppingBagIcon fontSize="small" />} iconPosition="start" label={isAr ? "طلباتي" : "My Orders"} />
          <Tab icon={<AccountBalanceWalletIcon fontSize="small" />} iconPosition="start" label={isAr ? "مدفوعاتي" : "My Payments"} />
        </Tabs>

        {/* ── Payments Tab ── */}
        {activeTab === 1 && <MyPaymentsTab isAr={isAr} />}

        {/* ── Orders Tab content (hidden when payments tab active) ── */}
        {activeTab === 0 && <>

        {/* ── Stats Cards ── */}
        {orders.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
            {[
              { label: isAr ? "إجمالي الطلبات" : "Total Orders", value: orders.length, color: "#d5aa2f", bg: "#fffbeb" },
              { label: isAr ? "مكتملة"         : "Completed",    value: deliveredCount,  color: "#16a34a", bg: "#f0fdf4" },
              { label: isAr ? "قيد الانتظار"   : "Pending",      value: pendingCount,    color: "#d97706", bg: "#fffbeb" },
              { label: isAr ? "إجمالي الإنفاق" : "Total Spent",  value: `${totalSpent.toFixed(0)} ${isAr ? "ج.م" : "EGP"}`, color: "#7c3aed", bg: "#f5f3ff" },
            ].map((s) => (
              <Paper
                key={s.label}
                elevation={0}
                sx={{
                  flex: "1 1 160px",
                  p: 2.5,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "grey.100",
                  bgcolor: s.bg,
                  textAlign: "center",
                }}
              >
                <Typography variant="h5" fontWeight={900} sx={{ color: s.color, mb: 0.5 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {s.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* ── Empty State ── */}
        {orders.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: { xs: 4, md: 8 }, borderRadius: 4, textAlign: "center", border: "1px solid", borderColor: "grey.100", bgcolor: "#fff" }}
          >
            <Box sx={{ width: 100, height: 100, mx: "auto", mb: 3, borderRadius: "50%", bgcolor: "grey.50", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingBagIcon sx={{ fontSize: 52, color: "grey.300" }} />
            </Box>
            <Typography variant="h5" fontWeight={800} mb={1}>{t('no_orders')}</Typography>
            <Typography color="text.secondary" mb={4}>{t('try_best_sellers')}</Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/products")}
              sx={{
                py: 1.5, px: 5, fontWeight: 800, borderRadius: 3, textTransform: "none",
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                boxShadow: "0 4px 16px rgba(213,170,47,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)" },
              }}
            >
              {t('show_all_products')}
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {orders.map((o, index) => {
              const order   = o || {};
              const items   = order.items || [];
              const checkout = order.checkout || {};
              const orderId = order.order_id || order.order_number;
              const st      = getStatus(order.status);
              const StatusIcon = st.Icon;

              return (
                <Paper
                  key={orderId}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: "grey.100",
                    overflow: "hidden",
                    bgcolor: "#fff",
                    transition: "box-shadow 0.25s ease",
                    animation: `fadeInUp 0.45s ease-out ${index * 0.07}s both`,
                    "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.08)" },
                    "@keyframes fadeInUp": {
                      from: { opacity: 0, transform: "translateY(16px)" },
                      to:   { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  {/* ── Order Header ── */}
                  <Box
                    sx={{
                      px: 3, py: 2,
                      background: "linear-gradient(135deg, #1c1c1c 0%, #2d2d2d 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Typography variant="subtitle1" fontWeight={800} color="#fff">
                        #{order.order_number || orderId}
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.6,
                          px: 1.5, py: 0.4, borderRadius: 10,
                          bgcolor: st.bg, border: `1px solid ${st.border}`,
                        }}
                      >
                        <StatusIcon sx={{ fontSize: 14, color: st.color }} />
                        <Typography variant="caption" fontWeight={800} sx={{ color: st.color }}>
                          {st.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="rgba(255,255,255,0.5)" fontWeight={600}>
                        {order.created_at}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ textAlign: "end" }}>
                        <Typography variant="caption" color="rgba(255,255,255,0.5)" fontWeight={700} sx={{ display: "block" }}>
                          {isAr ? "الإجمالي" : "Total"}
                        </Typography>
                        <Typography variant="h6" fontWeight={900}
                          sx={{ background: "linear-gradient(135deg, #d5aa2f, #e6c76a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                        >
                          {order.total} {isAr ? "ج.م" : "EGP"}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openOrderDialog(orderId)}
                        sx={{
                          borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.78rem",
                          bgcolor: "rgba(213,170,47,0.15)", color: "#d5aa2f",
                          border: "1px solid rgba(213,170,47,0.3)",
                          boxShadow: "none",
                          "&:hover": { bgcolor: "rgba(213,170,47,0.25)", boxShadow: "none" },
                        }}
                      >
                        {t('view_details')}
                      </Button>

                      {isOrderCancelable(order.status) && (
                        <Button
                          size="small"
                          startIcon={canceling[orderId] ? null : <CancelIcon />}
                          onClick={() => handleCancelOrder(orderId)}
                          disabled={!!canceling[orderId]}
                          sx={{
                            borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.78rem",
                            bgcolor: "rgba(220,38,38,0.12)", color: "#ef4444",
                            border: "1px solid rgba(220,38,38,0.2)",
                            boxShadow: "none",
                            "&:hover": { bgcolor: "rgba(220,38,38,0.2)", boxShadow: "none" },
                          }}
                        >
                          {canceling[orderId] ? <CircularProgress size={14} sx={{ color: "#ef4444" }} /> : t('cancel')}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* ── Price Strip ── */}
                  <Box
                    sx={{
                      display: "flex", flexWrap: "wrap", gap: 0,
                      borderBottom: "1px solid", borderColor: "grey.100",
                    }}
                  >
                    {[
                      { label: t('subtotal'), value: `${order.subtotal || 0} ${isAr ? "ج.م" : "EGP"}`, color: "text.primary" },
                      { label: t('discount'),  value: `−${order.discount || 0} ${isAr ? "ج.م" : "EGP"}`, color: "success.main" },
                      { label: t('shipping'),  value: `${order.shipping || 0} ${isAr ? "ج.م" : "EGP"}`, color: "text.primary" },
                      { label: t('payment'),   value: order.payment_method, isChip: true },
                    ].map((cell) => (
                      <Box
                        key={cell.label}
                        sx={{
                          flex: "1 1 120px", px: 2.5, py: 1.5,
                          borderRight: "1px solid", borderColor: "grey.100",
                          "&:last-child": { borderRight: "none" },
                        }}
                      >
                        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ display: "block", mb: 0.3, textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
                          {cell.label}
                        </Typography>
                        {cell.isChip ? (
                          <Chip label={cell.value} size="small" sx={{ bgcolor: "rgba(213,170,47,0.12)", color: "#b08c26", fontWeight: 800, height: 22, fontSize: "0.72rem" }} />
                        ) : (
                          <Typography variant="body2" fontWeight={800} color={cell.color}>{cell.value}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>

                  {/* ── Address Strip ── */}
                  {(checkout.phone || checkout.address) && (
                    <Box
                      sx={{
                        px: 3, py: 1.5, bgcolor: "#fafafa",
                        display: "flex", gap: 3, flexWrap: "wrap",
                        borderBottom: "1px solid", borderColor: "grey.100",
                      }}
                    >
                      {checkout.phone && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          📞 {checkout.phone}
                        </Typography>
                      )}
                      {checkout.address && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          📍 {checkout.address}
                        </Typography>
                      )}
                      {checkout.notes && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          📝 {checkout.notes}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* ── Items ── */}
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="caption" color="text.disabled" fontWeight={800} sx={{ display: "block", mb: 2, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
                      {t('items')} · {items.length}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {items.map((it, idx) => {
                        const prod = it.product || {};
                        const photo = prod?.main_photo || prod?.images?.[0]?.image || "";
                        const imageSrc = buildImageSrc(photo);
                        const returnKey = `${orderId}_${it.product_id}`;

                        return (
                          <Box
                            key={it.product_id + "-" + idx}
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              borderRadius: 3,
                              border: "1px solid",
                              borderColor: "grey.100",
                              transition: "bgcolor 0.2s",
                              "&:hover": { bgcolor: "#fafafa" },
                            }}
                          >
                            <Avatar
                              variant="rounded"
                              src={imageSrc}
                              alt={getName(prod) || ""}
                              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
                              sx={{ width: 72, height: 72, borderRadius: 2.5, flexShrink: 0, border: "1px solid", borderColor: "grey.100" }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                sx={{ mb: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {getName(prod) || `${t('product')} ${it.product_id}`}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 1 }}>
                                <Chip label={`×${it.quantity}`} size="small"
                                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "rgba(213,170,47,0.1)", color: "#b08c26" }} />
                                <Chip label={`${it.price} ${isAr ? "ج.م" : "EGP"}`} size="small"
                                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "rgba(22,163,74,0.08)", color: "#15803d" }} />
                                <Chip label={`= ${it.total} ${isAr ? "ج.م" : "EGP"}`} size="small"
                                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "grey.100", color: "text.secondary" }} />
                                {it.size && (
                                  <Chip label={`${t('size')}: ${it.size?.size || it.size?.name || it.size}`} size="small"
                                    sx={{ height: 20, fontSize: "0.7rem", bgcolor: "grey.100" }} />
                                )}
                                {it.color && (
                                  <Chip label={`${t('color')}: ${it.color?.color_en || it.color?.color_ar || it.color}`} size="small"
                                    sx={{ height: 20, fontSize: "0.7rem", bgcolor: "grey.100" }} />
                                )}
                              </Box>

                              {Number(prod?.retrun_status) === 1 && isOrderReturnable(order.status) && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={returning[returnKey] ? null : <AssignmentReturnIcon />}
                                  onClick={() => handleReturn(orderId, it.product_id)}
                                  disabled={!!returning[returnKey]}
                                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.75rem", height: 28 }}
                                >
                                  {returning[returnKey] ? <CircularProgress size={14} /> : t('return')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Additional Sections */}
        {orders.length > 0 && (
          <Box sx={{ mt: 6, display: "flex", flexDirection: "column", gap: 4 }}>
            <MyReviews orders={orders} />
            <SavedProducts />
          </Box>
        )}

        </>}
      </Container>

      <OrderDialog open={dialogOpen} onClose={closeOrderDialog} orderId={dialogOrderId} />
    </Box>
  );
}

// helper: build full image URL using MEDIA_BASE_URL when photo is a relative path
const buildImageSrc = (photo) => {
  if (!photo) return "/placeholder.png";
  if (/^https?:\/\//i.test(photo)) return photo;
  const base = (MEDIA_BASE_URL || "").replace(/\/$/, "");
  const path = photo.replace(/^\/+/, "");
  if (base) return `${base}/${path}`;
  return resolveImageUrl(photo);
};
